import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsClientsLoaded,
  IsClientsLoading,
  load,
  setIsClientsLoading,
} from "../redux/reducers/clientsList.reducer";

/**
 * Idempotent hook — loads the clients list (used for authorization checks +
 * clientsIds lookup) exactly once per session.
 *
 * Used to live in App.tsx's DataWrapper which fired on every login. Now called
 * from each client sub-hook (useClientContacts, useClientWork, useClientBillingInfo,
 * useClientDetails, etc.) so a deep-link to /clients/:id/contacts (or any
 * sub-route) still populates clientsIds for the authorization guard.
 *
 * Without this, deep-linked client sub-pages hang forever waiting on
 * isClientsLoaded, which lets MSAL background token renewal fire and (in
 * headless Playwright) fall back to acquireTokenRedirect.
 */
export const useClientsList = () => {
  const dispatch = useDispatch();
  const { rolesLoaded } = useSelector(selectUserRoles);
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const isClientsLoading = useSelector(IsClientsLoading);

  useEffect(() => {
    if (!rolesLoaded || isClientsLoaded || isClientsLoading) return;

    (async () => {
      dispatch(setIsClientsLoading(true));
      const clientsInfo = await ClientsService.getClients();
      dispatch(load(clientsInfo));
    })();
  }, [rolesLoaded, isClientsLoaded, isClientsLoading]); // eslint-disable-line react-hooks/exhaustive-deps
};
